import { Column, Header, Row } from '@/components/SettingsLayout';
import { Button, Icon, IconType, Input, Text } from '@/ui';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.css';
import CompaniesTable from './CompaniesTable';
import { useCallback, useState } from 'react';
import { debounce } from '@/utils/helpers/debounce';
import SkeletonTable from '@/components/SkeletonLoader/SkeletonTable';
import { useGetUser } from '@/hooks';
import CompaniesFilter from './components/CompaniesFilter';
import CreateCompanyModal from './components/CreateCompanyModal';
import { skipToken } from '@reduxjs/toolkit/query';
import { usePaginationFilter } from '@/hooks/usePaginationFilter';
import { Pagination } from '@/components';
import { CompanyFilter, useGetAllCompaniesQuery } from '@/store/slices/companySlice';
import { useLocation, useNavigate } from 'react-router-dom';
import { FINAL_USERS } from '@/enums';

export const companyRef = '/cdk/companies';
function Companies() {
  const { t } = useTranslation();
  const [, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const { company } = useGetUser();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { currentUser } = useGetUser();
  const { filter, setFilter, page, setPage, perPage, setPerPage } =
    usePaginationFilter<CompanyFilter>({
      search: '',
      limit: 25,
      offset: 0,
    });
  const { data, isLoading, isError, refetch } = useGetAllCompaniesQuery(
    company?._id
      ? {
          id: company._id,
          filter,
        }
      : skipToken,
  );

  const handleChange = (name: string, value: any) => {
    setFilter((prevState) => {
      return { ...prevState, [name]: value };
    });
  };

  const saveInput = useCallback((input: string) => {
    const lowercasedInput = input.toLowerCase();
    setSearchQuery(lowercasedInput);
  }, []);

  const debouncedSaveInput = debounce(saveInput, 500);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    debouncedSaveInput(event.target.value);
  };

  const addCompanyModal = () => {
    navigate(`${pathname}?mode=create`);
  };

  const finalUser = FINAL_USERS.includes(currentUser?.type);

  if (isLoading)
    return (
      <Column gapSize='24'>
        <Header>
          <h1>{t('Companies.title')}</h1>
          <div className={styles.searchInput}>
            <Input
              size='small'
              variant={'search'}
              value={inputValue}
              placeholder={t('Companies.search_text')}
              onChange={handleSearch}
            />
          </div>
        </Header>
        <SkeletonTable />
      </Column>
    );

  if (isError) {
    const message = 'Error fetching companies';
    throw new Error(message);
  }

  return (
    <Column gapSize='12'>
      <CreateCompanyModal onCompanyCreated={refetch} />
      <Header>
        <Text size='ll' weight='semi-bold'>
          {t('Companies.company_list')}
        </Text>
        {!finalUser && (
          <Button type='button' variant='primary' icon={true} onClick={addCompanyModal}>
            <Row align='center' gapSize='8'>
              <Icon type={IconType.Plus} width={20} height={20} />
              <span>{t('Shared.add_new')}</span>
            </Row>
          </Button>
        )}
      </Header>
      <Column gapSize='8'>
        <CompaniesFilter handleChange={handleChange} />
        {data?.data && data?.data.length > 0 ? (
          <CompaniesTable companies={data.data} />
        ) : (
          <Row justify='justify-center'>
            <Text size='s' weight='light'>
              {t('Companies.no_companies_yet')}
            </Text>
          </Row>
        )}
      </Column>
      {data?.count !== undefined && data?.count > 0 && data?.count >= filter?.limit && (
        <Pagination
          page={page}
          totalPages={Math.ceil(data?.count / Number(perPage))}
          setPage={setPage}
          perPage={String(perPage)}
          setPerPage={setPerPage}
        />
      )}
    </Column>
  );
}

export default Companies;
